import { createContext, useContext, useEffect, useState } from "react";
import { IS_SERVER } from "../../utils/is-server";

import { LanguagesApp } from "./languagesApp";

type ContextType = {
  language: LanguagesApp;
  setLanguage: (lang: LanguagesApp) => void;
};

const initialValues: ContextType = {
  language: LanguagesApp.Arabic,
  setLanguage: () => {},
};

const langLSKey = "lang";

type IProps = {
  children: React.ReactNode;
};

export const LanguageContext = createContext<ContextType>(initialValues);

export const LanguageProvider: React.FC<IProps> = ({ children }) => {
  const [language, setLanguage] = useState<LanguagesApp>(LanguagesApp.Arabic);

  const getLangLocal = () => {
    if (!IS_SERVER) {
      const langLocal = localStorage.getItem(langLSKey);

      return langLocal ? JSON.parse(langLocal) : null;
    }
    return null;
  };

  const setLangLocal = (lang: LanguagesApp) => {
    localStorage.setItem(langLSKey, JSON.stringify(lang));
  };

  const handleSetLanguage = (lang: LanguagesApp) => {
    setLangLocal(lang);
    setLanguage(lang);
  };

  const handleInitLanguage = () => {
    const langLocal = getLangLocal();

    if (langLocal) {
      handleSetLanguage(langLocal);
    }
  };

  useEffect(() => {
    handleInitLanguage();
  }, []);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage: handleSetLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): ContextType => {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
